/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { UPDATE_MEDIA_PLAN_DRAFT_STEP } from "@gql/mmm-media-plan";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_step_tracker,
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList,
  type UpdateMediaPlanDraftStep,
  type UpdateMediaPlanDraftStepVariables,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";

import { useMediaPlanData } from "./use-media-plan";

export const useUpdateMediaPlanDraftStep = ({
  draftStep,
  selectedMediaPlan,
}: {
  draftStep: Exclude<
    keyof FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_step_tracker,
    "__typename"
  >;
  selectedMediaPlan: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList;
}) => {
  const { setMediaPlanList } = useMediaPlanData();
  const [
    updateMediaPlanDraftStep,
    { loading: isUpdateMediaPlanDraftStepLoading },
  ] = useNorthbeamMutation<
    UpdateMediaPlanDraftStep,
    UpdateMediaPlanDraftStepVariables
  >(UPDATE_MEDIA_PLAN_DRAFT_STEP, {
    update: (_cache, { data }) => {
      if (data) {
        setMediaPlanList((prevMediaPlanList) => {
          if (data.updateMediaPlanDraftStep.id) {
            return prevMediaPlanList.map((mediaPlan) => {
              if (mediaPlan.id === data.updateMediaPlanDraftStep.id) {
                return data.updateMediaPlanDraftStep;
              }
              return mediaPlan;
            });
          }
          return prevMediaPlanList;
        });
      }
    },
  });

  const draftStepData = selectedMediaPlan.step_tracker[draftStep];

  const verify = async (verified_data: unknown[]) =>
    updateMediaPlanDraftStep({
      variables: {
        id: selectedMediaPlan.media_plan_draft_id!,
        draft_step: draftStep,
        verified: true,
        verified_data,
        pending_data: draftStepData.pending_data,
      },
    });

  const unverify = async () =>
    updateMediaPlanDraftStep({
      variables: {
        id: selectedMediaPlan.media_plan_draft_id!,
        draft_step: draftStep,
        verified: false,
        verified_data: [],
        pending_data: draftStepData.pending_data,
      },
    });

  const addPendingData = async (newPendingData: string) =>
    updateMediaPlanDraftStep({
      variables: {
        id: selectedMediaPlan.media_plan_draft_id!,
        draft_step: draftStep,
        verified: false,
        verified_data: [],
        pending_data: [...draftStepData.pending_data, newPendingData],
      },
    });

  const removePendingData = async (index: number) =>
    updateMediaPlanDraftStep({
      variables: {
        id: selectedMediaPlan.media_plan_draft_id!,
        draft_step: draftStep,
        verified: false,
        verified_data: [],
        pending_data: [
          ...draftStepData.pending_data.slice(0, index),
          ...draftStepData.pending_data.slice(index + 1),
        ],
      },
    });

  return {
    verify,
    unverify,
    addPendingData,
    removePendingData,
    isUpdateMediaPlanDraftStepLoading,
  };
};
